import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { BlogCategory, BlogPost, HydratedBlogPost } from '../src/types/blog';
import { rankRelatedPosts } from '../lib/relatedPosts';
import { safeErrorMessage } from '../utils/sanitize';

const BLOG_POSTS_COLLECTION = 'blog_posts';

const getBlogPostsRef = () => collection(db, BLOG_POSTS_COLLECTION);

const hydratePost = (
  snap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>
): HydratedBlogPost => {
  const data = snap.data() as BlogPost;
  const publishedAt =
    data.published_at instanceof Timestamp
      ? data.published_at.toDate()
      : (data.published_at as unknown as Date);
  const updatedAt = data.updated_at
    ? data.updated_at instanceof Timestamp
      ? data.updated_at.toDate()
      : (data.updated_at as unknown as Date)
    : undefined;

  return {
    id: snap.id,
    title: data.title,
    slug: data.slug,
    category: data.category,
    published_at: publishedAt,
    updated_at: updatedAt,
    excerpt: data.excerpt,
    body: data.body,
    featured_image_url: data.featured_image_url,
    seo_title: data.seo_title,
    status: data.status,
    // Faceted tags (item 10) — copied straight through; undefined on untagged
    // posts until population (separate step). getRelatedPosts degrades gracefully.
    styles: data.styles,
    regions: data.regions,
    audience: data.audience,
    ageGroups: data.ageGroups,
  };
};

// Short-lived cache of the FULL published list. getRelatedPosts re-reads the
// whole corpus on every blog-post view; caching it for a couple of minutes
// collapses those repeat reads within a browsing session (Phase 1 item 14).
// Only the unbounded (limitCount === undefined) result is cached; bounded
// queries stay direct. Blog content comes from a separate pipeline and changes
// slowly, so a short TTL is safe.
let publishedPostsCache: { posts: HydratedBlogPost[]; expiresAt: number } | null = null;
const PUBLISHED_POSTS_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

export const getPublishedPosts = async (
  limitCount?: number
): Promise<HydratedBlogPost[]> => {
  if (limitCount === undefined && publishedPostsCache && publishedPostsCache.expiresAt > Date.now()) {
    return [...publishedPostsCache.posts];
  }
  try {
    const constraints: QueryConstraint[] = [
      where('status', '==', 'published'),
      orderBy('published_at', 'desc'),
    ];
    if (limitCount !== undefined && limitCount > 0) {
      constraints.push(limit(limitCount));
    }

    const q = query(getBlogPostsRef(), ...constraints);
    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(hydratePost);
    if (limitCount === undefined) {
      publishedPostsCache = { posts, expiresAt: Date.now() + PUBLISHED_POSTS_CACHE_TTL_MS };
    }
    return posts;
  } catch (error) {
    console.error(
      '[blogService] Failed to fetch published posts:',
      safeErrorMessage(error)
    );
    // Re-throw so callers can distinguish a fetch FAILURE from a genuine
    // empty result. Previously this returned [], collapsing the two states
    // into one indistinguishable "no posts" empty-state (item 25).
    throw error;
  }
};

export const getPublishedPostsByCategory = async (
  category: BlogCategory,
  limitCount?: number
): Promise<HydratedBlogPost[]> => {
  try {
    const constraints: QueryConstraint[] = [
      where('status', '==', 'published'),
      where('category', '==', category),
      orderBy('published_at', 'desc'),
    ];
    if (limitCount !== undefined && limitCount > 0) {
      constraints.push(limit(limitCount));
    }

    const q = query(getBlogPostsRef(), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(hydratePost);
  } catch (error) {
    console.error(
      `[blogService] Failed to fetch posts for category "${category}":`,
      safeErrorMessage(error)
    );
    // Re-throw so callers can distinguish failure from empty (item 25).
    // RelatedPosts already catches this and fails silently (→ null); the
    // listing pages surface it as a distinct error state + toast.
    throw error;
  }
};

export const getPostBySlug = async (
  slug: string
): Promise<HydratedBlogPost | null> => {
  if (!slug) return null;

  try {
    const ref = doc(db, BLOG_POSTS_COLLECTION, slug);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;

    const data = snap.data() as BlogPost;
    if (data.status !== 'published') return null;

    return hydratePost(snap);
  } catch (error) {
    // Firestore rules gate reads to status=='published', so a missing OR
    // unpublished slug is denied (resource null / not published). Treat that
    // as not-found so the page shows a clean 404, not an error state + toast.
    if ((error as { code?: string })?.code === 'permission-denied') return null;
    console.error(
      `[blogService] Failed to fetch post "${slug}":`,
      safeErrorMessage(error)
    );
    // Re-throw so callers can distinguish a real fetch FAILURE from a
    // genuine "post not found" (item 44, mirroring item 25 on the index/
    // category services). Legitimate not-found cases above still return null.
    throw error;
  }
};

/**
 * getRelatedPosts — posts related to `post`, best first, excluding `post`.
 *
 * Fetches ALL published posts once and scores them IN MEMORY via
 * rankRelatedPosts (lib/relatedPosts). In-memory scoring is deliberate: it
 * avoids Firestore array-contains-any limits and needs NO new composite index
 * at this corpus size — it reuses the existing status+published_at read.
 *
 * Degrades gracefully: a post with no tags (or no tagged peers) falls back to
 * same-category + recency — identical to the previous RelatedPosts behaviour.
 * Throws on fetch failure so callers can distinguish failure from empty
 * (item 25); RelatedPosts catches and fails silently to null.
 */
export const getRelatedPosts = async (
  post: HydratedBlogPost,
  limitCount = 3
): Promise<HydratedBlogPost[]> => {
  const allPublished = await getPublishedPosts();
  return rankRelatedPosts(post, allPublished, limitCount);
};
